import {Card, DataStatus, RadioItem} from 'platform/components';
import {Clickable, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {filter, isNil, isNotNil, not, uniq} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import {
  GetServiceCaseOrderJobsApiResponse,
  useGetServiceCaseOrderJobsQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
} from '@dms/api/workshop';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {SelectedJobType} from '../../types/SelectedJobType';

interface MoveRequestItemOrderProps extends TestIdProps {
  serviceCaseId: string;
  id: string | Nullish;
  selectedJob: SelectedJobType | Nullish;
  onJobSelect: (jobId: string | Nullish) => void;
  hiddenServiceJob?: string | null;
  movingItemIds?: string[];
  withUncategorizedWithoutItems?: boolean;
  /** Greys out every job, for example while the move runs. */
  isDisabled?: boolean;
}

type ServiceCaseOrderJob = NonNullable<GetServiceCaseOrderJobsApiResponse>[number];

// A job without an id cannot be a move target: the move request needs serviceMoveToJobId.
const isJobSelectable = (job: ServiceCaseOrderJob) =>
  isNotNil(job.id) && job.movingItems?.isAvailableForMove !== false;

export function MoveRequestItemOrder(props: MoveRequestItemOrderProps) {
  const {
    data: serviceOrder,
    isLoading: isServiceOrderLoading,
    isError: isServiceOrderError,
  } = useGetServiceOrderQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.id ?? '',
    },
    {skip: isNil(props.id)}
  );
  const {
    data: serviceOrderVariant,
    isLoading: isServiceOrderVariantLoading,
    isError: isServiceOrderVariantError,
  } = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNil(serviceOrder?.serviceOrderVariantId)}
  );
  const {
    data: serviceCaseOrderJobs,
    isLoading: isServiceCaseOrderJobsLoading,
    isError: isServiceCaseOrderJobsError,
  } = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.id ?? '',
      movingItemIds: props.movingItemIds,
      withUncategorizedWithoutItems: props.withUncategorizedWithoutItems,
    },
    {skip: isNil(props.id)}
  );

  const serviceCaseOrderJobsToShow = filter(
    (job) => job?.id !== props.hiddenServiceJob && not(job?.servicePackageDetails?.isLock),
    serviceCaseOrderJobs ?? []
  );

  // BE resolves availability per order, so every blocked job carries the same reasons.
  const moveRestrictionMessages = uniq(
    serviceCaseOrderJobsToShow.flatMap((job) =>
      job?.movingItems?.isAvailableForMove === false
        ? (job.movingItems.moveRestrictionReasons ?? []).flatMap((reason) => reason.message ?? [])
        : []
    )
  );

  const isJobDisabled = (job: ServiceCaseOrderJob) =>
    Boolean(props.isDisabled) || not(isJobSelectable(job));

  return (
    <Show when={isNonEmptyArray(serviceCaseOrderJobsToShow)}>
      <Card variant="inlineWhite">
        <DataStatus
          isLoading={isServiceOrderVariantLoading || isServiceOrderLoading}
          isError={isServiceOrderVariantError || isServiceOrderError}
          minHeight={41}
        >
          <VStack spacing={4}>
            <HStack spacing={2}>
              <Heading size={4} data-testid={suffixTestId('orderName', props)}>
                {serviceOrder?.name}
              </Heading>
              <Heading size={4}>•</Heading>
              <Text data-testid={suffixTestId('orderVariantName', props)}>
                {serviceOrderVariant?.name}
              </Text>
            </HStack>
            <DataStatus
              isLoading={isServiceCaseOrderJobsLoading}
              isError={isServiceCaseOrderJobsError}
              minHeight={31}
            >
              <VStack spacing={3}>
                {moveRestrictionMessages.map((message, index) => (
                  <HStack key={message} spacing={1} align="center">
                    <Icon value="action/info_outline" size={4} />
                    <Text size="xSmall" data-testid={suffixTestId(`restriction-[${index}]`, props)}>
                      {message}
                    </Text>
                  </HStack>
                ))}
                {serviceCaseOrderJobsToShow?.map((job, index) => (
                  <Show key={job.id ?? index} when={job}>
                    <Clickable
                      isDisabled={isJobDisabled(job)}
                      onClick={() => props.onJobSelect(job.id)}
                      data-testid={suffixTestId(`job-[${index}]`, props)}
                    >
                      <Card variant="inlineGrey">
                        <HStack spacing={2}>
                          <RadioItem
                            value={
                              isNotNil(job.id) &&
                              job.id === props.selectedJob?.jobId &&
                              props.id === props.selectedJob?.orderId
                            }
                            isDisabled={isJobDisabled(job)}
                            data-testid={suffixTestId(`job-[${index}]`, props)}
                          />
                          <Heading
                            size={4}
                            color={isJobDisabled(job) ? 'tertiary' : undefined}
                            data-testid={suffixTestId(`job-[${index}]`, props)}
                          >
                            {job.name}
                          </Heading>
                        </HStack>
                      </Card>
                    </Clickable>
                  </Show>
                ))}
              </VStack>
            </DataStatus>
          </VStack>
        </DataStatus>
      </Card>
    </Show>
  );
}
